// Dev-only test panel state and actions (spec §16). Everything works by
// mutating the mock db; the panel UI lives in src/components/dev/TestPanel.tsx.
import { loadDb, saveDb } from './db';
import { mockNow } from './clock';
import { clearOtpSessions } from './handlers/otp';
import { addIncomingCredit, settlePendingPayments } from './handlers/payments';

export interface TestPanelState {
  nextOutcome?: 'SUCCESS' | 'PENDING' | 'FAILED';
  debitsFrozen: boolean;
  skipCooling: boolean;
  removeTimeLimits: boolean;
  newDeviceUntil?: number;
  clockOffsetMs: number;
  pendingCount: number;
  balancePaise: number;
  sgbWindowOpen: boolean;
  networkChoiceEnabled: boolean;
  nextBillFails: boolean;
}

export const getTestPanelState = (): TestPanelState => {
  const db = loadDb();
  return {
    nextOutcome: db.limits.nextOutcome,
    debitsFrozen: db.limits.debitsFrozen,
    skipCooling: db.limits.skipCooling,
    removeTimeLimits: db.limits.removeTimeLimits,
    newDeviceUntil: db.limits.newDeviceUntil,
    clockOffsetMs: db.limits.clockOffsetMs,
    pendingCount: db.payments.filter((p) => p.status === 'PENDING').length,
    balancePaise: db.balancePaise,
    sgbWindowOpen: db.phase4.sgbWindowOpen,
    networkChoiceEnabled: db.phase4.networkChoiceEnabled,
    nextBillFails: db.phase4.nextBillFails,
  };
};

export const setNextOutcome = (outcome?: 'SUCCESS' | 'PENDING' | 'FAILED'): void => {
  const db = loadDb();
  db.limits.nextOutcome = outcome;
  saveDb(db);
};

export const setDebitsFrozen = (frozen: boolean): void => {
  const db = loadDb();
  db.limits.debitsFrozen = frozen;
  saveDb(db);
};

export const simulateNewDevice = (): void => {
  const db = loadDb();
  db.limits.newDeviceUntil = mockNow(db) + 24 * 3_600_000;
  saveDb(db);
};

export const setSkipCooling = (skip: boolean): void => {
  const db = loadDb();
  db.limits.skipCooling = skip;
  saveDb(db);
};

/** "Remove time limits" (§16): skips the 30-minute wait, the 24-hour caps, the
 *  new-device cap and all lockouts, and clears any running lock. */
export const setRemoveTimeLimits = (remove: boolean): void => {
  const db = loadDb();
  db.limits.removeTimeLimits = remove;
  if (remove) {
    db.limits.mpinLockedUntil = undefined;
    db.limits.paymentsLockedUntil = undefined;
    db.limits.mpinFailures = 0;
    db.limits.paymentOtpFailures = 0;
    clearOtpSessions();
  }
  saveDb(db);
};

export const setSgbWindow = (open: boolean): void => {
  const db = loadDb();
  db.phase4.sgbWindowOpen = open;
  saveDb(db);
};

export const setNetworkChoice = (enabled: boolean): void => {
  const db = loadDb();
  db.phase4.networkChoiceEnabled = enabled;
  saveDb(db);
};

export const setNextBillFails = (fails: boolean): void => {
  const db = loadDb();
  db.phase4.nextBillFails = fails;
  saveDb(db);
};

export const moveClockForward = (ms: number): void => {
  const db = loadDb();
  db.limits.clockOffsetMs += ms;
  saveDb(db);
};

export { addIncomingCredit, settlePendingPayments };
